import React, { useEffect } from 'react';
import { setPageTitle } from '../../utils/title';
import { trackEvent } from '../../utils/analytics';
import { useCart } from '../../context/CartContext';
import Breadcrumb from '../../components/Breadcrumb';
import './Solutions.scss';

const Compliance = () => {
  const { addToCart, removeFromCart, isInCart } = useCart();

  useEffect(() => {
    setPageTitle('IT Compliance Solutions');
  }, []);

  const handleCartAction = (solutionType, title) => {
    const itemId = `compliance-${solutionType}`;
    const isItemInCart = isInCart(itemId);

    if (isItemInCart) {
      trackEvent('remove_from_cart', 'Compliance Solutions', {
        solution_name: 'IT Compliance',
        solution_type: solutionType,
        source_page: 'compliance_solutions',
      });
      removeFromCart(itemId);
    } else {
      trackEvent('add_to_cart', 'Compliance Solutions', {
        solution_name: 'IT Compliance',
        solution_type: solutionType,
        source_page: 'compliance_solutions',
      });
      addToCart({
        id: itemId,
        title,
        type: 'Compliance Solution',
      });
    }
  };

  const renderCartButton = (solutionType, title) => {
    const itemId = `compliance-${solutionType}`;
    const isItemInCart = isInCart(itemId);

    return (
      <button
        className="card__link card__link--cart"
        onClick={() => handleCartAction(solutionType, title)}
      >
        {isItemInCart ? 'Remove from shopping cart' : 'Add to shopping cart'}
      </button>
    );
  };

  const breadcrumbs = [
    { path: '/', label: 'Home' },
    { path: '/solutions', label: 'Solutions' },
    { path: '/solutions/compliance', label: 'IT Compliance' },
  ];

  return (
    <div className="solutions-page">
      <Breadcrumb items={breadcrumbs} />
      <section className="page-header section section--light">
        <div className="container">
          <p className="section__subtitle">IT Compliance Program Implementation</p>
          <h1 className="page-header__title">IT Compliance Solutions</h1>
          <p className="page-header__description">
            We help you establish and implement a comprehensive IT compliance program that meets
            regulatory requirements and industry standards. Our solutions cover SOC 2, HIPAA, NIST
            800-171, NIST SP 800-53, NIST AI RMF, ISO 42001:2023, and GDPR.
          </p>
          <blockquote className="section__quote">
            Our compliance solutions are designed to help you achieve and maintain compliance with
            minimal disruption to your business operations. We work with you to understand your
            specific needs and implement solutions that align with your business goals.
          </blockquote>
        </div>
      </section>

      <section className="solutions-content section section--light">
        <div className="container">
          <div className="card solution-card">
            <h3 className="card__title">SOC 2 Compliance Program</h3>
            <p className="card__description">
              We help you establish and implement a SOC 2 compliance program, including gap
              analysis, policy development, control implementation, and audit preparation. Our
              approach ensures you meet SOC 2 requirements while maintaining operational efficiency.
            </p>
            {renderCartButton('soc2', 'SOC 2 Compliance Program')}
          </div>

          <div className="card solution-card">
            <h3 className="card__title">HIPAA Compliance Program</h3>
            <p className="card__description">
              We assist in implementing HIPAA compliance requirements, including security risk
              analysis, policy development, technical safeguards implementation, and staff training.
              Our solutions ensure protection of PHI while enabling efficient healthcare operations.
            </p>
            {renderCartButton('hipaa', 'HIPAA Compliance Program')}
          </div>

          <div className="card solution-card">
            <h3 className="card__title">NIST Compliance Program</h3>
            <p className="card__description">
              We help implement NIST 800-171, NIST SP 800-53, and NIST AI RMF requirements,
              including security controls, risk assessment, continuous monitoring, and
              documentation. Our approach ensures comprehensive compliance with NIST standards.
            </p>
            {renderCartButton('nist', 'NIST Compliance Program')}
          </div>

          <div className="card solution-card">
            <h3 className="card__title">GDPR and ISO Compliance Program</h3>
            <p className="card__description">
              We assist in implementing GDPR and ISO 42001:2023 requirements, including data
              protection measures, privacy policies, AI governance frameworks, and documentation.
              Our solutions ensure compliance while maintaining operational efficiency.
            </p>
            {renderCartButton('gdpr_iso', 'GDPR and ISO Compliance Program')}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Compliance;
